/**
 * Knowledge Graph Visualization Component
 *
 * Interactive graph visualization using ReactFlow
 */
import React, { useCallback, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  NodeTypes,
  EdgeTypes,
  MarkerType,
  Panel,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { knowledgeGraphStore } from '@/stores/KnowledgeGraphStore'
import { GraphVisualizationNode, GraphVisualizationEdge, NodeType, RelationType } from '@/types'
import CustomGraphNode from './CustomGraphNode'
import CustomGraphEdge from './CustomGraphEdge'

interface GraphVisualizationProps {
  graphId: string
  onNodeClick?: (nodeId: string) => void
  onEdgeClick?: (edgeId: string) => void
  onNodeDoubleClick?: (nodeId: string) => void
  height?: string
  readonly?: boolean
}

// Node type colors
const NODE_TYPE_COLORS: Record<NodeType, string> = {
  [NodeType.CONCEPT]: '#3b82f6', // blue
  [NodeType.ENTITY]: '#10b981', // green
  [NodeType.EVENT]: '#f59e0b', // amber
  [NodeType.ATTRIBUTE]: '#8b5cf6', // violet
  [NodeType.DOCUMENT]: '#06b6d4', // cyan
  [NodeType.AGENT]: '#ec4899', // pink
  [NodeType.TASK]: '#f97316', // orange
  [NodeType.RULE]: '#84cc16', // lime
  [NodeType.CUSTOM]: '#6b7280', // gray
}

// Relation type colors
const RELATION_TYPE_COLORS: Record<RelationType, string> = {
  [RelationType.IS_A]: '#3b82f6',
  [RelationType.HAS_A]: '#10b981',
  [RelationType.PART_OF]: '#f59e0b',
  [RelationType.RELATED_TO]: '#8b5cf6',
  [RelationType.DEPENDS_ON]: '#ef4444',
  [RelationType.CAUSES]: '#dc2626',
  [RelationType.LEADS_TO]: '#f97316',
  [RelationType.SIMILAR_TO]: '#06b6d4',
  [RelationType.OPPOSITE_OF]: '#64748b',
  [RelationType.BEFORE]: '#84cc16',
  [RelationType.AFTER]: '#22c55e',
  [RelationType.CREATED_BY]: '#ec4899',
  [RelationType.USED_BY]: '#a855f7',
  [RelationType.CUSTOM]: '#6b7280',
}

const GraphVisualization: React.FC<GraphVisualizationProps> = observer(({
  graphId,
  onNodeClick,
  onEdgeClick,
  onNodeDoubleClick,
  height = '600px',
  readonly = false,
}) => {
  const { visualizationData, isLoading, error } = knowledgeGraphStore

  // Convert visualization data to ReactFlow format
  const convertToReactFlowNodes = useCallback((graphNodes: GraphVisualizationNode[]): Node[] => {
    return graphNodes.map((node, index) => ({
      id: node.id,
      type: 'custom',
      position: {
        x: node.x || (index % 5) * 250,
        y: node.y || Math.floor(index / 5) * 150
      },
      data: {
        label: node.label,
        nodeType: node.node_type,
        properties: node.properties,
        color: NODE_TYPE_COLORS[node.node_type],
      },
    }))
  }, [])

  const convertToReactFlowEdges = useCallback((graphEdges: GraphVisualizationEdge[]): Edge[] => {
    return graphEdges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'custom',
      animated: false,
      label: edge.label || edge.relation_type,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: RELATION_TYPE_COLORS[edge.relation_type],
      },
      data: {
        relationType: edge.relation_type,
        weight: edge.weight,
        color: RELATION_TYPE_COLORS[edge.relation_type],
      },
      style: {
        stroke: RELATION_TYPE_COLORS[edge.relation_type],
      },
    }))
  }, [])

  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  // Update nodes and edges when visualization data changes
  useEffect(() => {
    if (visualizationData) {
      const flowNodes = convertToReactFlowNodes(visualizationData.nodes)
      const flowEdges = convertToReactFlowEdges(visualizationData.edges)
      setNodes(flowNodes)
      setEdges(flowEdges)
    }
  }, [visualizationData, convertToReactFlowNodes, convertToReactFlowEdges, setNodes, setEdges])

  // Load visualization data
  useEffect(() => {
    knowledgeGraphStore.fetchVisualization(graphId, {
      depth: 2,
      limit: 100,
    })
  }, [graphId])

  const onConnect = useCallback(
    (params: Connection) => {
      if (!readonly) {
        setEdges((eds) => addEdge(params, eds))
      }
    },
    [readonly, setEdges]
  )

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (onNodeClick) {
        onNodeClick(node.id)
      }
    },
    [onNodeClick]
  )

  const handleNodeDoubleClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (onNodeDoubleClick) {
        onNodeDoubleClick(node.id)
      }
    },
    [onNodeDoubleClick]
  )

  const handleEdgeClick = useCallback(
    (_event: React.MouseEvent, edge: Edge) => {
      if (onEdgeClick) {
        onEdgeClick(edge.id)
      }
    },
    [onEdgeClick]
  )

  // Custom node and edge types
  const nodeTypes: NodeTypes = useMemo(() => ({
    custom: CustomGraphNode,
  }), [])

  const edgeTypes: EdgeTypes = useMemo(() => ({
    custom: CustomGraphEdge,
  }), [])

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200"
        style={{ height }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading graph visualization...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="flex items-center justify-center bg-red-500/10 rounded-lg border border-red-500/20"
        style={{ height }}
      >
        <div className="text-center">
          <p className="text-red-600">Error loading visualization: {error}</p>
        </div>
      </div>
    )
  }

  if (!visualizationData || visualizationData.nodes.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200"
        style={{ height }}
      >
        <div className="text-center">
          <p className="text-gray-600">No nodes in this graph yet</p>
          <p className="text-sm text-gray-500 mt-2">Add nodes to start building your knowledge graph</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ height, width: '100%' }} className="bg-white rounded-lg border border-gray-200">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onEdgeClick={handleEdgeClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        attributionPosition="bottom-left"
      >
        <Background />
        <Controls />
        <MiniMap
          nodeColor={(node) => node.data.color}
          nodeStrokeWidth={3}
          zoomable
          pannable
        />
        <Panel position="top-right" className="bg-white p-2 rounded shadow-md text-sm">
          <div className="space-y-1">
            <div className="font-semibold text-gray-700">Graph Stats</div>
            <div className="text-gray-600">
              Nodes: {visualizationData?.total_nodes || 0}
            </div>
            <div className="text-gray-600">
              Edges: {visualizationData?.total_edges || 0}
            </div>
          </div>
        </Panel>
      </ReactFlow>
    </div>
  )
})

GraphVisualization.displayName = 'GraphVisualization'

export default GraphVisualization
